import { useCallback, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ProtectedRoute } from '../components/auth/index.js';
import { CcCommandMatrixPanel } from '../components/features/cc-command-matrix/index.js';
import { TabButton, TabGroup } from '../components/ui/Tabs/index.js';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { Picker } from '@cortex/web-design-system/components/ui/Picker';
import { useCcUsageTeams } from '../hooks/useCcUsage.js';
import { useCcCommandMatrixMembers, useCcCommandMatrixProjects } from '../hooks/useCcEvents.js';
import {
  CC_COMMAND_MATRIX_AXIS_VALUES,
  parseCcCommandMatrixSearch,
  resolveCcCommandMatrixSearch,
  stripCcCommandMatrixSearchDefaults,
  type CcCommandMatrixSearch,
} from './-cc-command-matrix-search.js';

/** チーム Picker の「全チーム」を表す sentinel value (URL には出さない)。@graph-connects none */
const ALL_TEAMS = '__all__';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business AI 規定コマンド利用状況ダッシュボードページ。人 / PJ を行、スプレッドシート管理のマスタが定める工程を列にして、規定コマンド (Claude Code skill) の実行数を期間・チームで絞って表示する。イベントが無い人 (未セットアップ疑い) と規定コマンド 0 の人を区別して見せる。state は URL search params に sparse 同期する
 * @graph-connects useCcUsageTeams [calls] チーム絞り込みの選択肢取得
 * @graph-connects useCcCommandMatrixMembers [calls] 人 × 工程マトリクス取得
 * @graph-connects useCcCommandMatrixProjects [calls] PJ × 工程マトリクス取得
 * @graph-connects CcCommandMatrixPanel [calls] マトリクス描画
 * @graph-connects TabGroup [calls] 人 / PJ 軸切替タブ描画
 */
export const Route = createFileRoute('/cc-command-matrix')({
  component: CcCommandMatrixRoute,
  validateSearch: parseCcCommandMatrixSearch,
});

/**
 * ルートガード。ccUsage と同じスコープ (PI Div 所属者・admin)。
 * @graph-connects ProtectedRoute [calls] ページアクセス可否を判定しガードする
 */
function CcCommandMatrixRoute() {
  return (
    <ProtectedRoute pageKey="ccCommandMatrix">
      <CcCommandMatrixPage />
    </ProtectedRoute>
  );
}

/** @graph-connects none */
function CcCommandMatrixPage() {
  const { t } = useTranslation();

  const search = Route.useSearch();
  const resolved = useMemo(() => resolveCcCommandMatrixSearch(search), [search]);
  const navigate = useNavigate({ from: Route.fullPath });

  const updateSearch = useCallback(
    (patch: Partial<CcCommandMatrixSearch>) => {
      void navigate({
        search: (prev) => stripCcCommandMatrixSearchDefaults({ ...prev, ...patch }),
        replace: true,
      });
    },
    [navigate],
  );

  const { axis, range, team, includeCortex, hideNoData, showCompleted } = resolved;

  const { data: teamsData } = useCcUsageTeams({ range });
  const teamOptions = useMemo(() => {
    const options = (teamsData?.data ?? [])
      .map((item) => ({ value: item.teamName, label: item.teamName }))
      .sort((a, b) => a.label.localeCompare(b.label));
    // 選択中のチームが (期間内の利用が無く) 選択肢から落ちても、URL の値を表示できるよう残す。
    if (team && !options.some((option) => option.value === team)) {
      options.unshift({ value: team, label: team });
    }
    return [{ value: ALL_TEAMS, label: t('ccCommandMatrix.allTeams') }, ...options];
  }, [teamsData, team, t]);

  const periodOptions = [
    { value: '7d', label: t('time.last7Days') },
    { value: '14d', label: t('time.last14Days') },
    { value: '30d', label: t('time.last30Days') },
    { value: '90d', label: t('time.last90Days') },
    { value: '180d', label: t('time.last180Days') },
    { value: '365d', label: t('time.last1Year') },
  ];

  const queryParams = useMemo(() => ({ range, team, includeCortex }), [range, team, includeCortex]);
  const membersQuery = useCcCommandMatrixMembers(queryParams, axis === 'member');
  const projectsQuery = useCcCommandMatrixProjects(queryParams, axis === 'project');
  const activeQuery = axis === 'member' ? membersQuery : projectsQuery;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
            {t('ccCommandMatrix.title')}
          </h1>
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('ccCommandMatrix.description')}
          </p>
        </div>
        {activeQuery.data?.master ? (
          <a
            href={activeQuery.data.master.spreadsheetUrl}
            target="_blank"
            rel="noreferrer"
            className="text-sm underline text-[var(--color-accent-primary)] hover:text-[var(--color-accent-hover)] whitespace-nowrap"
          >
            {t('ccCommandMatrix.masterLink')}
          </a>
        ) : null}
      </header>
      <Card padding="none">
        <CardContent className="flex flex-wrap items-end gap-3 !mt-0 p-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-[var(--color-text-muted)]">
              {t('ccCommandMatrix.axisSwitchLabel')}
            </span>
            <TabGroup ariaLabel={t('ccCommandMatrix.axisSwitchLabel')}>
              {CC_COMMAND_MATRIX_AXIS_VALUES.map((value) => (
                <TabButton
                  key={value}
                  active={axis === value}
                  label={t(`ccCommandMatrix.axis.${value}`)}
                  onClick={() => {
                    updateSearch({ axis: value });
                  }}
                />
              ))}
            </TabGroup>
          </div>
          <div className="min-w-[160px] flex-1">
            <Picker
              label={t('ccCommandMatrix.periodSelectLabel')}
              value={range}
              options={periodOptions}
              onChange={(value) => {
                if (value) updateSearch({ range: value as CcCommandMatrixSearch['range'] });
              }}
            />
          </div>
          <div className="min-w-[200px] flex-1">
            <Picker
              label={t('ccCommandMatrix.teamSelectLabel')}
              value={team ?? ALL_TEAMS}
              options={teamOptions}
              onChange={(value) => {
                updateSearch({ team: value && value !== ALL_TEAMS ? value : undefined });
              }}
              searchable
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-[var(--color-text-secondary)] pb-2">
            <Checkbox
              checked={includeCortex}
              onChange={(event) => {
                updateSearch({ includeCortex: event.target.checked });
              }}
            />
            <span>{t('ccCommandMatrix.filters.includeCortex')}</span>
          </label>
          {axis === 'member' ? (
            <label className="flex items-center gap-2 cursor-pointer text-sm text-[var(--color-text-secondary)] pb-2">
              <Checkbox
                checked={hideNoData}
                onChange={(event) => {
                  updateSearch({ hideNoData: event.target.checked });
                }}
              />
              <span>{t('ccCommandMatrix.filters.hideNoData')}</span>
            </label>
          ) : (
            <label className="flex items-center gap-2 cursor-pointer text-sm text-[var(--color-text-secondary)] pb-2">
              <Checkbox
                checked={showCompleted}
                onChange={(event) => {
                  updateSearch({ showCompleted: event.target.checked });
                }}
              />
              <span>{t('ccCommandMatrix.filters.showCompleted')}</span>
            </label>
          )}
        </CardContent>
      </Card>
      <CcCommandMatrixPanel
        axis={axis}
        phases={activeQuery.data?.phases ?? []}
        memberRows={membersQuery.data?.rows ?? []}
        projectRows={projectsQuery.data?.rows ?? []}
        master={activeQuery.data?.master}
        isLoading={activeQuery.isLoading}
        isError={activeQuery.isError}
        hideNoData={hideNoData}
        showCompleted={showCompleted}
      />
    </div>
  );
}
